import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Text } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { Page, Row } from 'styles';
import { BackLink } from 'components/back-link';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useContractAgreements } from 'hooks/use-contract-agreements.hook';
import { ErrorPage } from 'components/errors';
import { AccountPlan, useAccountPlans } from 'hooks/use-account-plans.hook';
import { LmiTable } from 'components/lmi-table';
import { useUser } from 'hooks/use-user.hook';
import { formatDate } from '@perch-labs/warbler';
import { LoadingPage } from 'components/loading';
import { useLocation, useHistory } from 'react-router-dom';
import {
  AddDocumentButton,
  LmiStyledRowChild,
  SelfAttestationButton,
  StyledSubHeading,
} from './check-lmi-eligibility.style';
import { LmiContractError } from './components/lmi-contract-error/lmi-contract-error.component';
import { LmiContract } from './components/lmi-contract/lmi-contract.component';
import { AddProofDocUpload } from './components/add-proof-doc-upload';
import { fetchSanitizedUtilityAccounts } from '../fetch-sanitized-utility-accounts.utils';

const getCopy = copyFor('settings.checkLmiEligibility');
const { settings } = routes();

export const CheckLmiEligibility = () => {
  const location = useLocation();
  const history = useHistory();
  const { utilityAccountId, utilityAccountLMISourceTypeId } = useMemo(() => {
    if (!location.search)
      return { utilityAccountId: null, utilityAccountLMISourceTypeId: null };
    const searchParams = new URLSearchParams(location.search);
    return {
      utilityAccountId: Number(searchParams.get('utilityAccountId')) as
        | number
        | null,
      utilityAccountLMISourceTypeId: Number(
        searchParams.get('utilityAccountLMISourceTypeId')
      ) as number | null,
    };
  }, [location.search]);
  const addProofDocUploadRef = useRef<HTMLDivElement>(null);

  const [showProofDocUpload, setShowProofDocUpload] = useState(false);
  const [plans, setPlans] = useState<AccountPlan[]>([]);
  const [isRequestDisabled, setIsRequestDisabled] = useState(false);
  const [cooldownEndDate, setCooldownEndDate] = useState<Date | null>(null);
  const [selectedUtilityId, setSelectedUtilityId] = useState<number | null>(
    null
  );
  const [isSelfAttestationState, setIsSelfAttestationState] = useState(false);
  const [enableManualCaptureOfProofs, setEnableManualCaptureOfProofs] =
    useState(false);
  const ldClient = useLDClient();
  let firstUtilityAccountId = utilityAccountId;

  useEffect(() => {
    if (utilityAccountId && utilityAccountLMISourceTypeId) {
      setShowProofDocUpload(true);
      // Timeout is needed to ensure the ref is available
      setTimeout(() => {
        addProofDocUploadRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 0);
    }
  }, [utilityAccountId, utilityAccountLMISourceTypeId]);

  const sanitizedUtilityAccounts = useMemo(() => {
    return fetchSanitizedUtilityAccounts(plans);
  }, [plans]);

  const {
    data: planData,
    loading: planLoading,
    error: planError,
  } = useAccountPlans(0, 'active');

  if (planData && !utilityAccountId) {
    const firstUtilityAccount = planData?.plans[0]?.utilityAccounts[0];
    if (firstUtilityAccount) {
      firstUtilityAccountId = firstUtilityAccount.id;
    }
  }

  const { data: user, loading: userLoading, refetch: refetchUser } = useUser();

  const custom = useMemo(
    () => ({
      funnel: user?.originationSource?.funnel ?? 'communitySolar',
      state: planData?.plans
        .flatMap(plan => plan.utilityAccounts)
        .find(ua => ua.utility.id === selectedUtilityId)
        ?.utility.state.abbreviation.toLowerCase(),
      utilityId: selectedUtilityId!,
      userId: user?.id,
    }),
    [
      user?.originationSource?.funnel,
      selectedUtilityId,
      user?.id,
      planData?.plans,
    ]
  );

  useEffect(() => {
    if (ldClient && custom.utilityId && custom.userId && custom.state) {
      ldClient.identify({
        kind: 'user',
        key: `user_${custom.userId}_utility_${custom.utilityId}`,
        ...custom,
      });
    }
  }, [ldClient, custom]);

  useEffect(() => {
    if (planData) {
      setPlans(planData.plans as AccountPlan[]);
      const utilityAccountAsscoiatedWithActivePlan = planData.plans.find(
        plan => plan.utilityAccounts[0].id === firstUtilityAccountId
      );
      setSelectedUtilityId(
        utilityAccountAsscoiatedWithActivePlan
          ? utilityAccountAsscoiatedWithActivePlan.utilityAccounts[0].utility.id
          : planData?.plans[0]?.utilityAccounts[0].utility.id
      );
    }
  }, [planData, firstUtilityAccountId]);

  const selectedLmiProofDoc =
    user?.lmiProofDocs?.find(
      doc =>
        doc?.uaLmiSourceTypeId === utilityAccountLMISourceTypeId &&
        doc?.utilityAccountId === utilityAccountId
    ) || undefined;

  const {
    csLmiSelfAttestationState,
    enableManualCaptureOfProofs: enableManualCaptureOfProofsFlag,
  } = useFlags();

  const [isTableDataLoading, setIsTableDataLoading] = useState(userLoading);
  useEffect(() => {
    setIsTableDataLoading(userLoading);
  }, [userLoading]);

  const {
    data,
    error,
    loading,
    refetch: refetchAgreements,
  } = useContractAgreements();

  const displayFileUrl = contractAgreement => {
    const { contractVersionLanguage, contractVersion } = contractAgreement;
    return (
      contractAgreement.fileUrl ||
      contractVersionLanguage?.fileUrl ||
      contractVersion.fileUrl
    );
  };

  const lmiContract = data?.find(r => r.contractVersion.contractType === 'lmi');

  const getCooldownStatus = useCallback((): {
    isInCooldown: boolean;
    availableDate: Date | null;
  } => {
    if (!data) return { isInCooldown: false, availableDate: null };

    const lmiAgreements = data
      .filter(a => a.contractVersion.contractType === 'lmi')
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

    if (!lmiAgreements.length) {
      return { isInCooldown: false, availableDate: null };
    }

    const lastAgreementDate = new Date(lmiAgreements[0].createdAt);
    const cooldownEndDate = new Date(lastAgreementDate);
    cooldownEndDate.setDate(cooldownEndDate.getDate() + 15);

    const now = new Date();
    const isInCooldown = now < cooldownEndDate;

    return {
      isInCooldown,
      availableDate: isInCooldown ? cooldownEndDate : null,
    };
  }, [data]);

  const cooldownStatus = useMemo(
    () => getCooldownStatus(),
    [getCooldownStatus]
  );

  useEffect(() => {
    if (cooldownStatus.isInCooldown) {
      setIsRequestDisabled(true);
      setCooldownEndDate(cooldownStatus.availableDate);
    } else {
      setIsRequestDisabled(false);
      setCooldownEndDate(null);
    }
  }, [cooldownStatus]);

  const isMnLmiState = useMemo(() => {
    const stateAbbrs = planData?.plans.flatMap(
      plan => plan.utilityAccounts[0]?.utility.state.abbreviation
    );
    return (
      stateAbbrs?.includes('MN') && user?.originationSource?.funnel === 'mnLmi'
    );
  }, [planData, user]);

  const validProofDocs = useMemo(() => {
    return (user?.lmiProofDocs || []).filter(
      doc =>
        doc?.proofDocStatus === 'pending_review' ||
        doc?.proofDocStatus === 'verified'
    );
  }, [user?.lmiProofDocs]);

  const hasValidProofDocs = validProofDocs.length > 0;

  useEffect(() => {
    const selfAttestationEnabledStates = csLmiSelfAttestationState || [];
    setIsSelfAttestationState(
      selfAttestationEnabledStates.some(abbreviation => {
        const state = planData?.plans.flatMap(
          plan => plan.utilityAccounts[0]?.utility.state.abbreviation
        );
        // Check if the user is from MN LMI funnel
        if (state?.includes('MN')) {
          return (
            user?.originationSource?.funnel === 'mnLmi' && abbreviation === 'MN'
          );
        }
        return state?.includes(abbreviation);
      })
    );

    setEnableManualCaptureOfProofs(enableManualCaptureOfProofsFlag);
  }, [
    csLmiSelfAttestationState,
    enableManualCaptureOfProofsFlag,
    planData,
    user?.originationSource?.funnel,
  ]);

  const toggleAddDocument = useCallback(() => {
    setShowProofDocUpload(prevShowProofDocUpload => !prevShowProofDocUpload);
  }, []);

  const navigateToContractForm = useCallback(() => {
    const firstUtilityAccount = sanitizedUtilityAccounts[0];
    if (firstUtilityAccount) {
      history.push({
        pathname: `${settings}/check-lmi-eligibility/contract-form`,
        state: {
          utilityAccountId: firstUtilityAccount.value,
          utilityId: firstUtilityAccount.utilityId,
          state: firstUtilityAccount.state,
          stateId: firstUtilityAccount.stateId,
          zipCode: firstUtilityAccount.zipCode,
          utilityName: firstUtilityAccount.text,
          validProofDocs: isMnLmiState ? validProofDocs : undefined,
        },
      });
    }
  }, [history, sanitizedUtilityAccounts, isMnLmiState, validProofDocs]);

  if (planLoading) return <LoadingPage />;
  if (planError) return <ErrorPage />;

  return (
    <>
      <BackLink to={settings} />
      <Page maxWidth="800px">
        <Text textStyle="heading800" tag="h1" margin={{ bottom: '16px' }}>
          {getCopy('title')}
        </Text>
        <Text margin={{ bottom: '20px' }}>
          {enableManualCaptureOfProofs
            ? getCopy('descriptionIra')
            : getCopy('description')}
        </Text>
        {enableManualCaptureOfProofs && (
          <>
            <Text margin={{ bottom: '10px' }}>
              {getCopy('documentInformation.title')}
            </Text>
            <ol>
              <li>
                <Text> {getCopy('documentInformation.programName')}</Text>
              </li>
              <li>
                <Text> {getCopy('documentInformation.participantName')}</Text>
              </li>
              <li>
                <Text> {getCopy('documentInformation.date')}</Text>
              </li>
            </ol>
          </>
        )}
        <Text margin={{ bottom: '20px' }}>
          {getCopy('documentInformation.subContext')}
        </Text>
        <Text margin={{ bottom: '20px' }}>
          {getCopy('documentInformation.additonalInfo')}
        </Text>
        <StyledSubHeading>
          {getCopy('eligibilityDocuments.title')}
        </StyledSubHeading>
        <Row>
          <LmiStyledRowChild>
            <LmiTable
              data={user?.lmiProofDocs || []}
              default={getCopy('eligibilityDocuments.default')}
              loading={isTableDataLoading}
            />
            <AddDocumentButton onClick={toggleAddDocument}>
              {getCopy('eligibilityDocuments.button')}
            </AddDocumentButton>
            <Text
              margin={{
                top: '16px',
                bottom: showProofDocUpload ? '16px' : '0',
              }}
              color="secondary"
            >
              {getCopy('eligibilityDocuments.documentInfo')}
            </Text>
            {showProofDocUpload ? (
              <div ref={addProofDocUploadRef}>
                <AddProofDocUpload
                  toggleAddDocument={toggleAddDocument}
                  utilityDropDown={sanitizedUtilityAccounts}
                  refetchLmiDocs={refetchUser}
                  setIsTableDataLoading={setIsTableDataLoading}
                  data={user?.lmiProofDocs || []}
                  user={user}
                  selectedUtilityAccountId={utilityAccountId}
                  selectedUaLMISourceType={selectedLmiProofDoc?.proofDocName}
                />
              </div>
            ) : null}
          </LmiStyledRowChild>
        </Row>
        {isSelfAttestationState && (
          <>
            <StyledSubHeading>
              {getCopy('selfAttestation.title')}
            </StyledSubHeading>
            <Row>
              <LmiStyledRowChild>
                <Text margin={{ bottom: '24px' }} color="secondary">
                  {getCopy('selfAttestation.description')}
                </Text>
                {!loading && (
                  <>
                    {!lmiContract && (
                      <Text>{getCopy('selfAttestation.default')}</Text>
                    )}
                    {!!lmiContract && !error && (
                      <LmiContract
                        createdAt={lmiContract.createdAt}
                        displayName={lmiContract.contractVersion.displayName}
                        fileUrl={displayFileUrl(lmiContract)}
                      />
                    )}
                    {error && (
                      <LmiContractError
                        refetchAll={async () => {
                          await refetchAgreements();
                        }}
                      />
                    )}
                  </>
                )}
                {isMnLmiState && (
                  <Text
                    color="secondary"
                    margin={{ bottom: '16px' }}
                    data-testid="mn-proof-doc-info"
                  >
                    {hasValidProofDocs
                      ? getCopy(
                          'selfAttestationAdditionalInfo.withExistingProofDoc',
                          {
                            latestProofDocName:
                              validProofDocs[0]?.proofDocName || '',
                          }
                        )
                      : getCopy(
                          'selfAttestationAdditionalInfo.withoutExistingProofDoc'
                        )}
                  </Text>
                )}
                {cooldownEndDate && (
                  <Text
                    color="secondary"
                    margin={{ top: '16px' }}
                    data-testid="cooldown-message"
                  >
                    {getCopy('selfAttestationModal.cooldownMessage', {
                      date: formatDate(cooldownEndDate),
                    })}
                  </Text>
                )}
                <SelfAttestationButton
                  onClick={navigateToContractForm}
                  disabled={
                    isRequestDisabled || (isMnLmiState && !hasValidProofDocs)
                  }
                >
                  {getCopy('selfAttestation.button')}
                </SelfAttestationButton>
              </LmiStyledRowChild>
            </Row>
          </>
        )}
      </Page>
    </>
  );
};
